import { useCallback, useContext } from 'react';
import { ModuleContext } from './contexts.tsx';
import type { ModuleRegistry } from './useModule.ts';

/**
 * Typed inter-module link builder.
 *
 * Cross-module navigation has historically been hard-coded URL strings
 * (`navigate('/tyndo.equipment/assets')`), which silently breaks when a view
 * is renamed or a module changes its route shape. `useModuleLink()` resolves a
 * `{ moduleId, viewId }` pair to a URL through the module registry when one is
 * mounted (ModuleProvider / TyndoProvider), and falls back to the platform's
 * canonical URL convention when it isn't:
 *
 *   /{moduleId}/{viewSlug}        — viewSlug = last dot-segment of the viewId
 *   /{moduleId}                   — module landing (no viewId)
 *
 * `params` substitute `:token` segments in a registry-declared view path;
 * any params left over (or all of them on the canonical fallback) are
 * appended as query-string filters so deep links stay shareable.
 */

export interface ModuleLinkTarget {
  /** Owning module id, e.g. `tyndo.equipment`. */
  moduleId: string;
  /** Registered view id, namespaced (`equipment.view.assets`) or short (`assets`). */
  viewId?: string;
  /** Route `:token` substitutions; unmatched entries become query params. */
  params?: Record<string, string>;
}

export type BuildModuleLink = (target: ModuleLinkTarget) => string;

/** Last dot-segment of a namespaced viewId — mirrors the shell's URL slugs. */
function viewSlug(viewId: string): string {
  const idx = viewId.lastIndexOf('.');
  return idx >= 0 ? viewId.slice(idx + 1) : viewId;
}

function appendQueryParams(path: string, params: Record<string, string>): string {
  const entries = Object.entries(params);
  if (entries.length === 0) return path;
  const qs = new URLSearchParams(entries).toString();
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

/**
 * Pure resolver — usable outside React (registries, imperative orchestrators).
 * Pass the registry explicitly; `useModuleLink()` wires it from context.
 */
export function buildModuleLink(
  target: ModuleLinkTarget,
  registry?: ModuleRegistry | null,
): string {
  const { moduleId, viewId, params } = target;

  if (registry) {
    const module = registry.getModule(moduleId);
    if (module) {
      const base = module.baseRoute || `/${moduleId}`;
      if (!viewId) return base;
      // Try both the id as given and its short slug — modules register either.
      const view =
        registry.getView(moduleId, viewId) ?? registry.getView(moduleId, viewSlug(viewId));
      if (view) {
        let path = `${base.replace(/\/$/, '')}/${view.path.replace(/^\//, '')}`;
        const leftover: Record<string, string> = {};
        for (const [key, value] of Object.entries(params ?? {})) {
          const token = `:${key}`;
          if (path.includes(token)) {
            path = path.replace(token, encodeURIComponent(value));
          } else {
            leftover[key] = value;
          }
        }
        return appendQueryParams(path, leftover);
      }
    }
  }

  // Canonical fallback — matches the shell router's `/{moduleId}/{slug}` shape.
  const path = viewId ? `/${moduleId}/${viewSlug(viewId)}` : `/${moduleId}`;
  return appendQueryParams(path, params ?? {});
}

/**
 * Hook form. Reads the module registry from ModuleContext when available but
 * does NOT require a provider — outside one (or before the registry loads) it
 * degrades to the canonical URL convention, so chrome components like the
 * shell breadcrumbs can adopt it unconditionally.
 *
 * @example
 * const buildLink = useModuleLink();
 * navigate(buildLink({ moduleId: 'tyndo.equipment', viewId: 'equipment.view.assets' }));
 */
export function useModuleLink(): BuildModuleLink {
  const moduleContext = useContext(ModuleContext);
  const registry = moduleContext?.registry ?? null;
  return useCallback(
    (target: ModuleLinkTarget) => buildModuleLink(target, registry),
    [registry],
  );
}
